/**
 * 5th + Sunday — Custom Product Options
 *
 * Handles:
 *   - Swatches (image or hex, with +$X badge for monthly colors)
 *   - Image pickers (single or multi with max_select limit)
 *   - Dropdowns (with optional image preview)
 *   - Checkboxes, radio pills, text inputs
 *   - Charm toggle — hides charm section behind "Add charms" checkbox
 *   - Form validation before add-to-cart
 *
 * All selections write to hidden inputs:
 *   <input name="properties[Option Name]" value="...">
 * and reach the cart as line-item properties (no Shopify variant limit).
 */

(function () {
  'use strict';

  // ── INIT ────────────────────────────────────────────────────────

  function initFasOptions(root) {
    root = root || document;
    root.querySelectorAll('[data-fas-options]').forEach(initContainer);
  }

  function initContainer(container) {
    container.querySelectorAll('.fas-option-group').forEach(function (group) {
      var type = group.dataset.optionType;
      switch (type) {
        case 'swatches':     initSwatches(group);    break;
        case 'image_picker': initImagePicker(group); break;
        case 'dropdown':     initDropdown(group);    break;
        case 'checkboxes':   initCheckboxes(group);  break;
        case 'radio':        initRadioPills(group);  break;
        case 'text':         initTextInput(group);   break;
      }
    });
    initCharmToggle(container);
  }

  // ── CHARM TOGGLE ────────────────────────────────────────────────

  function initCharmToggle(container) {
    var toggle  = container.querySelector('[data-charm-toggle]');
    var section = container.querySelector('[data-charm-section]');
    if (!toggle || !section) return;

    toggle.addEventListener('change', function () {
      var open = this.checked;
      section.hidden      = !open;
      section.setAttribute('aria-hidden', String(!open));

      // Clear all charm selections when hiding the section
      if (!open) {
        section.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(function (input) {
          input.checked = false;
        });
        section.querySelectorAll('[data-option-input]').forEach(function (hidden) {
          hidden.value = '';
        });
        section.querySelectorAll('[data-selected-label]').forEach(function (el) {
          el.textContent = '';
        });
        section.querySelectorAll('[data-selected-count]').forEach(function (el) {
          el.textContent = '0';
        });
      }
    });
  }

  // ── SWATCHES ────────────────────────────────────────────────────

  function initSwatches(group) {
    var radios   = group.querySelectorAll('input[type="radio"]');
    var hidden   = group.querySelector('[data-option-input]');
    var labelEl  = group.querySelector('[data-selected-label]');

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (hidden) hidden.value = this.value;
        if (labelEl) {
          var swatch = this.closest('.fas-swatch');
          labelEl.textContent = swatch ? swatch.dataset.label : this.value;
        }
      });
    });
  }

  // ── IMAGE PICKER ────────────────────────────────────────────────

  function initImagePicker(group) {
    var isMulti  = group.querySelector('[data-multi]') &&
                   group.querySelector('[data-multi]').dataset.multi === 'true';
    var maxSel   = parseInt(group.dataset.maxSelect || '0', 10);
    var hidden   = group.querySelector('[data-option-input]');
    var inputs   = group.querySelectorAll('input');
    var countEl  = group.querySelector('[data-selected-count]');

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        // Enforce max_select for multi pickers
        if (isMulti && maxSel > 0) {
          var checked = group.querySelectorAll('input:checked');
          if (checked.length > maxSel) {
            this.checked = false;
            showMaxSelectHint(group, maxSel);
            return;
          }
        }

        if (!hidden) return;

        if (isMulti) {
          var vals = Array.from(group.querySelectorAll('input:checked')).map(function (i) {
            return i.value;
          });
          hidden.value = vals.join(', ');
          if (countEl) countEl.textContent = vals.length;
        } else {
          hidden.value = this.checked ? this.value : '';
        }
      });
    });
  }

  function showMaxSelectHint(group, max) {
    var hint = group.querySelector('.fas-max-select-hint');
    if (!hint) return;
    hint.classList.add('fas-max-select-hint--error');
    hint.querySelector('[data-selected-count]').textContent = max;
    clearTimeout(hint._timeout);
    hint._timeout = setTimeout(function () {
      hint.classList.remove('fas-max-select-hint--error');
    }, 2000);
  }

  // ── DROPDOWN ────────────────────────────────────────────────────

  function initDropdown(group) {
    var select  = group.querySelector('.fas-dropdown');
    var hidden  = group.querySelector('[data-option-input]');
    var preview = group.querySelector('[data-dropdown-preview]');
    if (!select) return;

    select.addEventListener('change', function () {
      var opt = this.options[this.selectedIndex];
      if (hidden) hidden.value = this.value;

      if (preview) {
        var imgSrc = opt.dataset.image || '';
        if (imgSrc && this.value !== '') {
          preview.querySelector('.fas-dropdown-preview__image').src = imgSrc;
          preview.querySelector('.fas-dropdown-preview__image').alt = opt.textContent.trim();
          preview.querySelector('.fas-dropdown-preview__label').textContent =
            opt.textContent.trim().split(' (+')[0];
          preview.style.display = 'flex';
        } else {
          preview.style.display = 'none';
        }
      }
    });
  }

  // ── CHECKBOXES ──────────────────────────────────────────────────

  function initCheckboxes(group) {
    var checkboxes = group.querySelectorAll('input[type="checkbox"]');
    var hidden     = group.querySelector('[data-option-input]');

    function sync() {
      if (!hidden) return;
      hidden.value = Array.from(checkboxes)
        .filter(function (c) { return c.checked; })
        .map(function (c) { return c.value; })
        .join(', ');
    }

    checkboxes.forEach(function (cb) { cb.addEventListener('change', sync); });
  }

  // ── RADIO PILLS ─────────────────────────────────────────────────

  function initRadioPills(group) {
    var radios = group.querySelectorAll('input[type="radio"]');
    var hidden = group.querySelector('[data-option-input]');

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (hidden) hidden.value = this.value;
      });
    });
  }

  // ── TEXT INPUT ──────────────────────────────────────────────────

  function initTextInput(group) {
    var input   = group.querySelector('.fas-text-input');
    var hidden  = group.querySelector('[data-option-input]');
    var counter = group.querySelector('[data-text-count]');
    if (!input) return;

    input.addEventListener('input', function () {
      if (hidden) hidden.value = this.value.trim();
      if (counter && this.maxLength > 0) {
        counter.textContent = this.value.length + ' / ' + this.maxLength;
      }
    });
  }

  // ── FORM VALIDATION ─────────────────────────────────────────────

  function validateFasOptions(form) {
    var valid    = true;
    var firstBad = null;

    form.querySelectorAll('.fas-option-group[data-required="true"]').forEach(function (group) {
      // Skip charm groups if the toggle is off
      var section = group.closest('[data-charm-section]');
      if (section && section.hidden) return;

      var hidden = group.querySelector('[data-option-input]');
      if (!hidden || hidden.value.trim() === '') {
        valid = false;
        group.classList.add('fas-option-group--error');
        if (!firstBad) firstBad = group;
      } else {
        group.classList.remove('fas-option-group--error');
      }
    });

    if (firstBad) {
      firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return valid;
  }

  // ── FORM SUBMIT HOOK ────────────────────────────────────────────

  function hookProductForm() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches('[action*="/cart/add"]')) return;
      if (!form.querySelector('[data-fas-options]')) return;

      if (!validateFasOptions(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  // ── STYLES ──────────────────────────────────────────────────────
  // Injected inline so they apply even before the external CSS loads.

  function injectStyles() {
    if (document.getElementById('fas-options-style')) return;
    var s = document.createElement('style');
    s.id  = 'fas-options-style';
    s.textContent = `
      /* ── Charm toggle ── */
      .fas-charm-toggle-wrap {
        margin-top: 2.4rem;
        padding-top: 2.4rem;
        border-top: 1px solid rgba(var(--color-foreground,62,58,54),0.12);
      }
      .fas-charm-toggle {
        display: inline-flex;
        align-items: center;
        gap: 1rem;
        cursor: pointer;
        user-select: none;
      }
      .fas-charm-toggle input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }
      .fas-charm-toggle__box {
        width: 1.8rem;
        height: 1.8rem;
        border: 1.5px solid rgba(var(--color-foreground,62,58,54),0.4);
        flex-shrink: 0;
        display: grid;
        place-items: center;
        transition: background-color .15s ease, border-color .15s ease;
      }
      .fas-charm-toggle input:checked ~ .fas-charm-toggle__box {
        background-color: rgb(var(--color-foreground,62,58,54));
        border-color:     rgb(var(--color-foreground,62,58,54));
      }
      .fas-charm-toggle input:checked ~ .fas-charm-toggle__box::after {
        content: '';
        width: .65rem;
        height: 1rem;
        border: 2px solid rgb(var(--color-background,243,240,236));
        border-top: none;
        border-left: none;
        transform: rotate(45deg) translateY(-1px);
      }
      .fas-charm-toggle__label {
        font-family: var(--font-body-family, sans-serif);
        font-size: 1.3rem;
        letter-spacing: .06em;
      }
      .fas-charm-toggle__note {
        font-family: var(--font-heading-family, serif);
        font-style: italic;
        font-size: 1.3rem;
        color: rgba(var(--color-foreground,62,58,54),.5);
      }

      /* ── Charm section (animated reveal) ── */
      .fas-charm-section {
        overflow: hidden;
      }
      .fas-charm-section[hidden] {
        display: none;
      }
      .fas-charm-section__intro {
        font-family: var(--font-body-family, sans-serif);
        font-size: 1.3rem;
        color: rgba(var(--color-foreground,62,58,54),.6);
        margin: 1.6rem 0 2.4rem;
        letter-spacing: .04em;
      }

      /* ── Max-select hint ── */
      .fas-max-select-hint {
        font-family: var(--font-body-family, sans-serif);
        font-size: 1.2rem;
        color: rgba(var(--color-foreground,62,58,54),.5);
        margin-top: .8rem;
        letter-spacing: .04em;
        transition: color .15s ease;
      }
      .fas-max-select-hint--error {
        color: #c0392b;
        font-weight: 500;
      }

      /* ── Price badges on swatches ── */
      .fas-swatch {
        position: relative;
      }
      .fas-swatch__price-badge {
        position: absolute;
        bottom: -1.8rem;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        font-family: var(--font-body-family, sans-serif);
        font-size: .95rem;
        letter-spacing: .04em;
        color: rgba(var(--color-foreground,62,58,54),.6);
        pointer-events: none;
      }
      .fas-swatches {
        margin-bottom: 2rem; /* room for the price badges */
      }

      /* ── Price on image option cards ── */
      .fas-image-option__price {
        display: block;
        font-style: italic;
        font-family: var(--font-heading-family, serif);
        font-size: 1.2rem;
        color: rgba(var(--color-foreground,62,58,54),.6);
        margin-top: .2rem;
      }

      /* ── Radio pills ── */
      .fas-radio-pills {
        display: flex;
        flex-wrap: wrap;
        gap: .8rem;
      }
      .fas-radio-pill { cursor: pointer; }
      .fas-radio-pill input[type="radio"] {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }
      .fas-radio-pill span {
        display: block;
        padding: .8rem 1.6rem;
        border: 1.5px solid rgba(var(--color-foreground,62,58,54),.3);
        font-family: var(--font-body-family, sans-serif);
        font-size: 1.2rem;
        letter-spacing: .06em;
        cursor: pointer;
        transition: border-color .15s ease, background-color .15s ease;
      }
      .fas-radio-pill input:checked + span {
        border-color: rgb(var(--color-foreground,62,58,54));
        background-color: rgba(var(--color-foreground,62,58,54),.06);
      }
      .fas-radio-pill--sold-out span {
        opacity: .4;
        cursor: not-allowed;
        text-decoration: line-through;
      }

      /* ── Sold-out states ── */
      .fas-swatch--sold-out       { opacity: .4; }
      .fas-swatch--sold-out input { cursor: not-allowed; }
      .fas-image-option--sold-out       { opacity: .4; }
      .fas-image-option--sold-out input { cursor: not-allowed; }

      /* ── Text input ── */
      .fas-text-input-wrap { position: relative; }
      .fas-text-input { width: 100%; }
      .fas-text-input__count {
        display: block;
        text-align: right;
        font-size: 1.1rem;
        color: rgba(var(--color-foreground,62,58,54),.45);
        margin-top: .4rem;
        font-family: var(--font-body-family, sans-serif);
      }

      /* ── Validation error state ── */
      .fas-option-group--error .fas-option-group__name { color: #c0392b; }
      .fas-option-group--error .fas-swatches,
      .fas-option-group--error .fas-image-picker,
      .fas-option-group--error .fas-dropdown,
      .fas-option-group--error .fas-checkboxes,
      .fas-option-group--error .fas-radio-pills {
        outline: 1.5px solid #c0392b;
        outline-offset: 4px;
      }
      .fas-option-group--error::before {
        content: 'Please make a selection';
        display: block;
        font-size: 1.2rem;
        color: #c0392b;
        margin-bottom: .4rem;
        font-family: var(--font-body-family, sans-serif);
        letter-spacing: .04em;
      }

      /* ── Checkbox option ── */
      .fas-checkbox-option__image {
        display: inline-block;
        vertical-align: middle;
        margin-right: .6rem;
        object-fit: cover;
      }
    `;
    document.head.appendChild(s);
  }

  // ── BOOT ────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    injectStyles();
    initFasOptions();
    hookProductForm();
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initFasOptions(e.target);
  });

  window.FasProductOptions = { init: initFasOptions };

}());
